import { HttpClient } from '@angular/common/http'
import { Injectable, OnDestroy } from '@angular/core'
import { BehaviorSubject, Observable, of, Subject, timer } from 'rxjs'
import { delay } from 'rxjs/operators'
import {
  Agent,
  AgentChatMessage,
  AgentContentType,
  AgentStatus,
} from '../../core/ddc-agents/models/agent.models'
import { DdcLoggerService } from '../log/ddc-logger.service'

const API_BASE = '/api/ai/agents'

@Injectable({ providedIn: 'root' })
export class AiAgentService implements OnDestroy {
  private agentsSubject = new BehaviorSubject<Agent[]>([])
  agents$ = this.agentsSubject.asObservable()

  private selectedAgentSubject = new BehaviorSubject<Agent | null>(null)
  selectedAgent$ = this.selectedAgentSubject.asObservable()

  private statusUpdateSubject = new Subject<{ agentId: number; status: AgentStatus }>()
  statusUpdate$ = this.statusUpdateSubject.asObservable()

  private ws: WebSocket | null = null
  private useDummyData = true // TODO: flip to false when backend is ready

  constructor(
    private logger: DdcLoggerService,
    private http: HttpClient,
  ) {
    this.loadAgents()
  }

  // ============================================================
  // Public API
  // ============================================================

  loadAgents(): void {
    if (this.useDummyData) {
      this.agentsSubject.next(this.buildDummyAgents())
      this.simulateRealtimeUpdates()
      return
    }
    this.http.get<Agent[]>(`${API_BASE}/`).subscribe({
      next: agents => this.agentsSubject.next(agents),
      error: err => this.logger.error('AiAgentService loadAgents error', err),
    })
  }

  selectAgent(agent: Agent): void {
    this.logger.info('AiAgentService selectAgent()', agent.name)
    this.selectedAgentSubject.next(agent)
  }

  createAgent(payload: {
    name: string
    topic: string
    sourceLang: string
    targetLang: string
    difficulty: string
    sessions: number
  }): Observable<Agent> {
    if (this.useDummyData) {
      const agents = this.agentsSubject.value
      const newAgent: Agent = {
        ...this.buildDummyAgents()[0],
        id: Date.now(),
        name: payload.name,
        status: AgentStatus.WORKING,
        statusMessage: 'Planning course...',
        chatHistory: [],
        createdContents: [],
      }
      this.agentsSubject.next([newAgent, ...agents])
      return of(newAgent).pipe(delay(300))
    }
    return this.http.post<Agent>(`${API_BASE}/`, payload)
  }

  sendMessage(agentId: number, content: string): Observable<AgentChatMessage> {
    if (this.useDummyData) {
      const userMsg: AgentChatMessage = {
        id: Date.now(),
        role: 'user',
        content,
        timestamp: new Date().toISOString(),
      }
      this.appendMessage(agentId, userMsg)

      // simulate agent reply
      timer(1200).subscribe(() => {
        const reply: AgentChatMessage = {
          id: Date.now() + 1,
          role: 'agent',
          content: `Great question! Based on your course progress, I recommend reviewing the vocabulary from Session 2 before our next session. Would you like me to create a practice quiz?`,
          timestamp: new Date().toISOString(),
        }
        this.appendMessage(agentId, reply)
      })
      return of(userMsg)
    }
    return this.http.post<AgentChatMessage>(`${API_BASE}/${agentId}/chat/`, { message: content })
  }

  // ============================================================
  // WebSocket (realtime agent status)
  // ============================================================

  connectWebSocket(): void {
    if (this.useDummyData) return
    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
    this.ws = new WebSocket(`${protocol}://${window.location.host}/ws/agents/`)

    this.ws.onmessage = (event: MessageEvent): void => {
      const data = JSON.parse(event.data)
      if (data.type === 'agent_status') {
        this.patchAgentStatus(data.agent_id, data.status, data.message)
      } else if (data.type === 'agent_message') {
        this.appendMessage(data.agent_id, data.message)
      } else if (data.type === 'agent_content_created') {
        this.loadAgents() // refresh full state
      }
    }
    this.ws.onclose = (): void => {
      this.logger.warn('AiAgentService websocket closed, reconnecting in 5s')
      timer(5000).subscribe(() => this.connectWebSocket())
    }
  }

  ngOnDestroy(): void {
    this.ws?.close()
  }

  // ============================================================
  // Internal helpers
  // ============================================================

  private patchAgentStatus(agentId: number, status: AgentStatus, message?: string): void {
    const agents = this.agentsSubject.value.map(a =>
      a.id === agentId ? { ...a, status, statusMessage: message } : a,
    )
    this.agentsSubject.next(agents)
    this.statusUpdateSubject.next({ agentId, status })

    const selected = this.selectedAgentSubject.value
    if (selected?.id === agentId) {
      this.selectedAgentSubject.next(agents.find(a => a.id === agentId) ?? null)
    }
  }

  private appendMessage(agentId: number, message: AgentChatMessage): void {
    const agents = this.agentsSubject.value.map(a =>
      a.id === agentId ? { ...a, chatHistory: [...a.chatHistory, message] } : a,
    )
    this.agentsSubject.next(agents)
    const selected = this.selectedAgentSubject.value
    if (selected?.id === agentId) {
      this.selectedAgentSubject.next(agents.find(a => a.id === agentId) ?? null)
    }
  }

  private simulateRealtimeUpdates(): void {
    // Simulate the "working" agent finishing after 15 seconds
    timer(15000).subscribe(() => {
      const working = this.agentsSubject.value.find(a => a.status === AgentStatus.WORKING)
      if (working) this.patchAgentStatus(working.id, AgentStatus.SUCCEEDED, 'Course ready!')
    })
  }

  // ============================================================
  // Dummy Data
  // ============================================================

  private buildDummyAgents(): Agent[] {
    const today = new Date()
    const d = (offset: number): string => {
      const dt = new Date(today)
      dt.setDate(dt.getDate() + offset)
      return dt.toISOString()
    }

    return [
      {
        id: 1,
        threadId: 'thread-abc-123',
        name: 'Prof. Lingo — German A1',
        avatarIcon: 'school',
        description: 'Everyday German for travelers',
        status: AgentStatus.SUCCEEDED,
        statusMessage: 'Course ready',
        createdAt: d(-14),
        lastActivityAt: d(-1),
        createdContents: [
          {
            id: 11,
            type: AgentContentType.DICTIONARY,
            title: 'Travel Basics Vocabulary',
            createdAt: d(-13),
          },
          {
            id: 12,
            type: AgentContentType.DICTIONARY,
            title: 'Restaurant & Food Words',
            createdAt: d(-10),
          },
          {
            id: 13,
            type: AgentContentType.NOTE,
            title: 'German Articles Explained',
            createdAt: d(-13),
          },
          { id: 14, type: AgentContentType.NOTE, title: 'Asking for Directions', createdAt: d(-8) },
          { id: 15, type: AgentContentType.QUIZ, title: 'Travel Vocab Quiz 1', createdAt: d(-12) },
          { id: 16, type: AgentContentType.QUIZ, title: 'Directions Quiz', createdAt: d(-7) },
          {
            id: 17,
            type: AgentContentType.FIGURE,
            title: 'Sentence Structure Diagram',
            createdAt: d(-11),
          },
        ],
        course: {
          id: 100,
          title: 'Everyday German for Travelers',
          topic: 'Travel German',
          sourceLang: 'ENG',
          targetLang: 'DEU',
          difficulty: 'beginner',
          totalSessions: 8,
          completedSessions: 5,
          progressPercent: 62,
          quizAverage: 84,
          quizResults: [
            { quizId: 15, title: 'Travel Vocab Quiz 1', score: 90, takenAt: d(-11) },
            { quizId: 16, title: 'Directions Quiz', score: 78, takenAt: d(-6) },
          ],
          sessions: [
            {
              id: 1,
              title: 'Greetings & Basics',
              date: d(-13),
              completed: true,
              moduleNumber: 1,
              contentIds: [11, 13],
            },
            {
              id: 2,
              title: 'Numbers & Time',
              date: d(-11),
              completed: true,
              moduleNumber: 2,
              contentIds: [15],
            },
            {
              id: 3,
              title: 'At the Restaurant',
              date: d(-9),
              completed: true,
              moduleNumber: 3,
              contentIds: [12],
            },
            {
              id: 4,
              title: 'Asking Directions',
              date: d(-7),
              completed: true,
              moduleNumber: 4,
              contentIds: [14, 16],
            },
            {
              id: 5,
              title: 'Shopping Phrases',
              date: d(-4),
              completed: true,
              moduleNumber: 5,
              contentIds: [],
            },
            {
              id: 6,
              title: 'Transport & Tickets',
              date: d(2),
              completed: false,
              moduleNumber: 6,
              contentIds: [],
            },
            {
              id: 7,
              title: 'Emergencies',
              date: d(5),
              completed: false,
              moduleNumber: 7,
              contentIds: [],
            },
            {
              id: 8,
              title: 'Final Review',
              date: d(8),
              completed: false,
              moduleNumber: 8,
              contentIds: [],
            },
          ],
          nextSession: {
            id: 6,
            title: 'Transport & Tickets',
            date: d(2),
            completed: false,
            moduleNumber: 6,
            contentIds: [],
          },
        },
        chatHistory: [
          {
            id: 1,
            role: 'agent',
            content:
              'Hallo! I am your German teacher. Your course "Everyday German for Travelers" is ready. We have 8 sessions planned — 5 completed so far. Great progress! 🎉',
            timestamp: d(-13),
          },
          { id: 2, role: 'user', content: 'Can you explain der/die/das again?', timestamp: d(-8) },
          {
            id: 3,
            role: 'agent',
            content:
              'Of course! I created a note "German Articles Explained" for you. In short: der = masculine, die = feminine, das = neuter. Check the note for memory tricks!',
            timestamp: d(-8),
          },
        ],
      },
      {
        id: 2,
        threadId: 'thread-def-456',
        name: 'Coach Kana — Japanese Kana',
        avatarIcon: 'translate',
        description: 'Hiragana & Katakana mastery',
        status: AgentStatus.WORKING,
        statusMessage: 'Creating session 3 content...',
        createdAt: d(-3),
        lastActivityAt: d(0),
        createdContents: [
          { id: 21, type: AgentContentType.DICTIONARY, title: 'Hiragana Basics', createdAt: d(-2) },
          { id: 22, type: AgentContentType.FIGURE, title: 'Kana Chart', createdAt: d(-2) },
        ],
        course: {
          id: 200,
          title: 'Hiragana & Katakana Mastery',
          topic: 'Japanese Writing',
          sourceLang: 'ENG',
          targetLang: 'JPN',
          difficulty: 'beginner',
          totalSessions: 6,
          completedSessions: 1,
          progressPercent: 16,
          quizAverage: 0,
          quizResults: [],
          sessions: [
            {
              id: 1,
              title: 'Hiragana Vowels',
              date: d(-2),
              completed: true,
              moduleNumber: 1,
              contentIds: [21],
            },
            {
              id: 2,
              title: 'Hiragana K-row',
              date: d(1),
              completed: false,
              moduleNumber: 2,
              contentIds: [],
            },
            {
              id: 3,
              title: 'Hiragana S-row',
              date: d(3),
              completed: false,
              moduleNumber: 3,
              contentIds: [],
            },
            {
              id: 4,
              title: 'Katakana Vowels',
              date: d(6),
              completed: false,
              moduleNumber: 4,
              contentIds: [],
            },
            {
              id: 5,
              title: 'Katakana Practice',
              date: d(9),
              completed: false,
              moduleNumber: 5,
              contentIds: [],
            },
            {
              id: 6,
              title: 'Full Kana Review',
              date: d(12),
              completed: false,
              moduleNumber: 6,
              contentIds: [],
            },
          ],
          nextSession: {
            id: 2,
            title: 'Hiragana K-row',
            date: d(1),
            completed: false,
            moduleNumber: 2,
            contentIds: [],
          },
        },
        chatHistory: [
          {
            id: 1,
            role: 'agent',
            content:
              'こんにちは! Your Kana course is being prepared. Session 1 is ready — start with the Hiragana vowels dictionary!',
            timestamp: d(-2),
          },
        ],
      },
      {
        id: 3,
        threadId: 'thread-ghi-789',
        name: 'Tutor Sol — Spanish Verbs',
        avatarIcon: 'menu_book',
        description: 'Past tense conjugation deep-dive',
        status: AgentStatus.ERROR,
        statusMessage: 'Content generation failed — retry available',
        createdAt: d(-1),
        lastActivityAt: d(-1),
        createdContents: [],
        course: null,
        chatHistory: [
          {
            id: 1,
            role: 'agent',
            content:
              'I hit an error while planning your Spanish verbs course. Please retry or adjust the topic.',
            timestamp: d(-1),
          },
        ],
      },
    ]
  }
}
